import * as Yup from 'yup';

import {
  Alert,
  AlertItem,
  Button,
  Form,
  PasswordField
} from '@vmware-react/clarity';
import React, { useCallback, useRef } from 'react';

import { AsyncOpState } from 'vrlcm-gui-types';
import { PASSWORD_REGEX } from 'vrlcm-gui-constants';
import { l10n } from '../i18n/i18nUtil';

export interface FirstBootPasswordFormData {
  password: string;
  confirmPassword: string;
}

interface Props {
  submitState: AsyncOpState;
  onSubmit: (formData: FirstBootPasswordFormData) => void;
  submitErrorMessage?: string;
  onChange: (formData: FirstBootPasswordFormData) => void;
}

const initialFirstBootPasswordFormData: FirstBootPasswordFormData = {
  password: '',
  confirmPassword: ''
};

const validationSchema = Yup.object().shape({
  password: Yup.string()
    .required(l10n('components.withAppRoot.passwordRequired'))
    .matches(PASSWORD_REGEX, l10n('components.withAppRoot.passwordRules')),
  confirmPassword: Yup.string()
    .required(l10n('components.withAppRoot.confirmPasswordRequired'))
    .oneOf(
      [Yup.ref('password')],
      l10n('components.withAppRoot.passwordsMustMatch')
    )
});

const FirstBootPasswordForm: React.FC<Props> = (props: Props) => {
  const { submitState, onSubmit, submitErrorMessage, onChange } = props;
  const initialValid = validationSchema.isValidSync(
    initialFirstBootPasswordFormData
  );
  const validityRef = useRef<boolean>(initialValid);
  const dataRef = useRef<FirstBootPasswordFormData>(
    initialFirstBootPasswordFormData
  );

  const updateBtnDisabled = submitState === AsyncOpState.Inprogress;

  const handleSubmit = useCallback(() => onSubmit(dataRef.current), [onSubmit]);

  const instantDataChangeUpdate = useCallback(() => {
    onChange(dataRef.current);
  }, [onChange]);

  return (
    <Form
      type="horizontal"
      autoComplete="off"
      initialValues={initialFirstBootPasswordFormData}
      validationSchema={validationSchema}
      enableReinitialize={true}
      isInitialValid={initialValid}
      onSubmit={handleSubmit}
      reqMsg={l10n('requiredFields')}
    >
      {formProps => {
        const { values, isValid, errors, touched } = formProps;
        validityRef.current = isValid;
        dataRef.current = values as FirstBootPasswordFormData;
        return (
          <>
            <PasswordField
              required={true}
              name="password"
              label={l10n('components.withAppRoot.newPassword')}
              placeholder=""
              onChange={instantDataChangeUpdate}
              error={!!(errors.password && touched.password)}
              errorMessage={errors.password as string}
            />
            <PasswordField
              required={true}
              name="confirmPassword"
              label={l10n('components.withAppRoot.confirmNewPassword')}
              placeholder=""
              onChange={instantDataChangeUpdate}
              error={!!(errors.confirmPassword && touched.confirmPassword)}
              errorMessage={errors.confirmPassword as string}
            />
            {submitState === AsyncOpState.Failed && (
              <Alert
                type="danger"
                items={[<AlertItem>{submitErrorMessage}</AlertItem>]}
              />
            )}
            <div style={{ margin: '21px 0px' }}>
              <Button
                block={true}
                type="submit"
                class="primary"
                disabled={updateBtnDisabled || !isValid}
              >
                {l10n('components.withAppRoot.updatePasswordButton')}
              </Button>
            </div>
          </>
        );
      }}
    </Form>
  );
};

export default FirstBootPasswordForm;
